'use client';

import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  PartitionFormat,
  PartitionSegmentType,
  SegmentType,
} from '@repo/api-gw-sdk';

import type { PathSegmentState } from '../IngestionPipelineWizardState';

interface SegmentConfigPanelProps {
  segment: PathSegmentState;
  primaryExists: boolean;
  readOnly: boolean;
  onChange: (updated: PathSegmentState) => void;
}

export function SegmentConfigPanel({
  segment,
  primaryExists,
  readOnly,
  onChange,
}: SegmentConfigPanelProps) {
  const handleTypeChange = (newType: SegmentType) => {
    if (newType === SegmentType.SEGMENT_TYPE_PARTITION) {
      onChange({
        ...segment,
        segmentType: newType,
        segmentPartitionType: primaryExists
          ? PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY
          : PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY,
      });
    } else {
      onChange({ ...segment, segmentType: newType });
    }
  };

  const segmentTypeLabel: Record<string, string> = {
    [SegmentType.SEGMENT_TYPE_STRING]: 'String',
    [SegmentType.SEGMENT_TYPE_PARTITION]: 'Partition',
    [SegmentType.SEGMENT_TYPE_WILDCARD]: 'Wildcard',
  };

  const partitionTypeLabel: Record<string, string> = {
    [PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY]: 'Primary',
    [PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY]: 'Secondary',
    [PartitionSegmentType.PARTITION_SEGMENT_TYPE_DATE]: 'Date',
  };

  const formatLabel: Record<string, string> = {
    [PartitionFormat.PARTITION_FORMAT_HIVE]: 'Hive (key=value)',
    [PartitionFormat.PARTITION_FORMAT_DIRECT]: 'Direct (value only)',
  };

  if (readOnly) {
    return (
      <Stack gap='8px' sx={{ p: '20px' }}>
        <ReadOnlyField
          label='Segment Type'
          value={segmentTypeLabel[segment.segmentType] ?? segment.segmentType}
        />
        {segment.segmentType === SegmentType.SEGMENT_TYPE_STRING && (
          <ReadOnlyField
            label='Value'
            value={segment.segmentStringValue || '-'}
          />
        )}
        {segment.segmentType === SegmentType.SEGMENT_TYPE_PARTITION && (
          <>
            <ReadOnlyField
              label='Partition Name'
              value={segment.segmentPartitionName || '-'}
            />
            <ReadOnlyField
              label='Partition Type'
              value={
                partitionTypeLabel[segment.segmentPartitionType] ??
                segment.segmentPartitionType
              }
            />
            <ReadOnlyField
              label='Format'
              value={
                formatLabel[segment.segmentPartitionFormat] ??
                segment.segmentPartitionFormat
              }
            />
          </>
        )}
        {segment.segmentType === SegmentType.SEGMENT_TYPE_WILDCARD && (
          <Typography variant='body2' color='text.secondary'>
            Wildcard matches any single path segment.
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap='16px' sx={{ p: '20px' }}>
      {/* Segment Type — always shown */}
      <FormControl size='small' sx={{ maxWidth: '200px' }}>
        <FormLabel>Segment Type</FormLabel>
        <Select
          size='small'
          value={segment.segmentType}
          onChange={(e) => handleTypeChange(e.target.value as SegmentType)}
        >
          <MenuItem value={SegmentType.SEGMENT_TYPE_STRING}>String</MenuItem>
          <MenuItem value={SegmentType.SEGMENT_TYPE_PARTITION}>
            Partition
          </MenuItem>
          <MenuItem value={SegmentType.SEGMENT_TYPE_WILDCARD}>
            Wildcard
          </MenuItem>
        </Select>
      </FormControl>

      {/* STRING: value input */}
      {segment.segmentType === SegmentType.SEGMENT_TYPE_STRING && (
        <FormControl size='small' sx={{ maxWidth: '300px' }}>
          <FormLabel>Value</FormLabel>
          <TextField
            size='small'
            value={segment.segmentStringValue}
            placeholder='e.g. data'
            onChange={(e) =>
              onChange({ ...segment, segmentStringValue: e.target.value })
            }
          />
        </FormControl>
      )}

      {/* PARTITION: name, type, format */}
      {segment.segmentType === SegmentType.SEGMENT_TYPE_PARTITION && (
        <Stack direction='row' gap='16px' flexWrap='wrap'>
          <FormControl
            size='small'
            sx={{ minWidth: '160px', flex: 1, maxWidth: '240px' }}
          >
            <FormLabel>Partition Name</FormLabel>
            <TextField
              size='small'
              value={segment.segmentPartitionName}
              placeholder='e.g. year'
              onChange={(e) =>
                onChange({ ...segment, segmentPartitionName: e.target.value })
              }
            />
          </FormControl>

          <FormControl size='small' sx={{ minWidth: '140px' }}>
            <FormLabel>Partition Type</FormLabel>
            <Select
              size='small'
              value={segment.segmentPartitionType}
              onChange={(e) =>
                onChange({
                  ...segment,
                  segmentPartitionType: e.target.value as PartitionSegmentType,
                })
              }
            >
              <MenuItem
                value={PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY}
                disabled={
                  primaryExists &&
                  segment.segmentPartitionType !==
                    PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY
                }
              >
                Primary
              </MenuItem>
              <MenuItem
                value={PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY}
              >
                Secondary
              </MenuItem>
              <MenuItem
                value={PartitionSegmentType.PARTITION_SEGMENT_TYPE_DATE}
              >
                Date
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: '160px' }}>
            <FormLabel>Format</FormLabel>
            <Select
              size='small'
              value={segment.segmentPartitionFormat}
              onChange={(e) =>
                onChange({
                  ...segment,
                  segmentPartitionFormat: e.target.value as PartitionFormat,
                })
              }
            >
              <MenuItem value={PartitionFormat.PARTITION_FORMAT_HIVE}>
                Hive (key=value)
              </MenuItem>
              <MenuItem value={PartitionFormat.PARTITION_FORMAT_DIRECT}>
                Direct (value only)
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      {/* WILDCARD: helper text */}
      {segment.segmentType === SegmentType.SEGMENT_TYPE_WILDCARD && (
        <Typography variant='body2' color='text.secondary'>
          Wildcard matches any single path segment.
        </Typography>
      )}
    </Stack>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction='row' gap='8px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '120px' }}
      >
        {label}
      </Typography>
      <Typography variant='body2'>{value}</Typography>
    </Stack>
  );
}
